/**
 * Un-election pre-flight (BLU-711 Part B Stage 2 PR3).
 *
 * Wraps `productSync.previewVariantSharedSkuLinkRemoval` — the read-only
 * twin of `removeVariantSharedSkuLinks` — behind one helper so the confirm
 * dialog has a single seam if the backend endpoint is reshaped. Returns
 * `null` on ANY failure: the dialog then degrades to generic consequence
 * copy rather than blocking the removal (a preview outage must not make
 * un-election impossible).
 *
 * PR #1667 review blocker: requests chunk to `LINK_STATE_GID_CHUNK` GIDs
 * (GET URL cap — see that constant's doc). Chunking changes the preview's
 * per-code math: each chunk's `remainingElectionCount` counts elections
 * outside THAT chunk's request, which includes the OTHER chunks' GIDs. The
 * merge corrects it: with E = total requested elections for a code across
 * all chunks, a chunk reporting `ownElected` of them sees
 * `remaining = trueRemaining + (E - ownElected)`, so
 * `trueRemaining = remaining - (E - ownElected)`. When trueRemaining is 0
 * but every chunk individually saw remaining > 0, no chunk resolved
 * `doomedMapping` — mapping existence is then unknown, and the code is
 * reported as doomed CONSERVATIVELY (over-warning beats promising a mapping
 * survives when it dies). Single-chunk requests (the common case) stay
 * exact.
 */
import type { trpc } from '@/lib/trpc';
import { chunkGids, type UnelectionPreview } from './shared-stock';

type TrpcUtils = ReturnType<typeof trpc.useUtils>;

/** A blank code whose LIVE mapping dies if the removal proceeds. */
export interface DoomedCode {
  baseSku: string;
}

export interface UnelectionConsequences {
  electedVariantCount: number;
  doomedCodes: DoomedCode[];
}

export async function fetchUnelectionConsequences(
  utils: TrpcUtils,
  shopifyVariantIds: string[],
): Promise<UnelectionConsequences | null> {
  try {
    const results: UnelectionPreview[] = await Promise.all(
      chunkGids(shopifyVariantIds).map((chunk) =>
        utils.productSync.previewVariantSharedSkuLinkRemoval.fetch({ shopifyVariantIds: chunk }),
      ),
    );

    let electedVariantCount = 0;
    // Stable merge order: first-appearance across chunks in request order.
    const perCode = new Map<
      string,
      { totalOwnElected: number; reports: { ownElected: number; remaining: number; doomed: boolean }[] }
    >();
    for (const result of results) {
      electedVariantCount += result.electedVariantCount;
      for (const code of result.codes) {
        let entry = perCode.get(code.baseSku);
        if (!entry) {
          entry = { totalOwnElected: 0, reports: [] };
          perCode.set(code.baseSku, entry);
        }
        entry.totalOwnElected += code.electedVariantIds.length;
        entry.reports.push({
          ownElected: code.electedVariantIds.length,
          remaining: code.remainingElectionCount,
          doomed: code.doomedMapping !== null,
        });
      }
    }

    const doomedCodes: DoomedCode[] = [];
    for (const [baseSku, entry] of perCode) {
      const trueRemaining = Math.min(
        ...entry.reports.map((r) => r.remaining - (entry.totalOwnElected - r.ownElected)),
      );
      if (trueRemaining <= 0 || entry.reports.some((r) => r.doomed)) {
        doomedCodes.push({ baseSku });
      }
    }
    return { electedVariantCount, doomedCodes };
  } catch {
    return null;
  }
}
